/**
 * BLE connection management and PSK handshake for OutdoorLink on-site provisioning.
 *
 * Handles connecting and disconnecting from a discovered BLE device, retrieving
 * its GATT service list, and running the validated PSK authentication handshake
 * over the UART GATT service. The handshake timing and flow are load-bearing —
 * they were confirmed against real OutdoorLink hardware and must not be changed.
 *
 * Contains:
 * - UART_SERVICE_UUID / UART_TX_CHAR / UART_RX_CHAR — GATT UUID constants
 * - hexStringToBytes   — hex string → Uint8Array (exported for testing)
 * - connectToDevice    — connect + optional disconnect callback
 * - disconnectFromDevice
 * - authenticateDevice — PSK handshake; returns { success, response?, error? }
 * - sendRelayCommand   — write relay toggle command over UART TX
 */

import { BleClient } from '@capacitor-community/bluetooth-le';

import { getSharedKey } from './sharedKey';

/** UART GATT service UUID — OutdoorLink BLE provisioning interface. */
export const UART_SERVICE_UUID = '90054c02-76f3-4892-9e84-5c9f2caeed3c';

/**
 * UART TX characteristic UUID — app writes to device.
 * Note: TX and RX share the same characteristic (write-without-response + notify).
 */
export const UART_TX_CHAR = 'ee3d5005-4de3-4f93-aebc-a3952ef6a22d';

/**
 * UART RX characteristic UUID — device notifies app.
 * Note: Intentionally equal to UART_TX_CHAR; the characteristic supports both
 * write-without-response and notify — validated against production hardware.
 */
export const UART_RX_CHAR = 'ee3d5005-4de3-4f93-aebc-a3952ef6a22d';

/** How long to wait for an "OK" response before failing auth. */
const AUTH_TIMEOUT_MS = 8000;

/** How often to check the accumulated response during the auth poll loop. */
const POLL_INTERVAL_MS = 200;

/** Brief pause after startNotifications before writing — lets the subscription register. */
const NOTIFY_SETTLE_MS = 100;

/**
 * Convert a hex string to a Uint8Array.
 *
 * @param {string} hexString - Hex digits, optionally with non-hex separators.
 * @returns {Uint8Array}
 */
export function hexStringToBytes(hexString) {
  const cleanHex = hexString.replace(/[^0-9a-fA-F]/g, '');
  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < cleanHex.length; i += 2) {
    bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Connect to a BLE device.
 *
 * @param {string} deviceId - The BLE device identifier from the scan result.
 * @param {Function} [onDisconnect] - Optional callback invoked when the device disconnects.
 * @returns {Promise<void>}
 */
export async function connectToDevice(deviceId, onDisconnect) {
  await BleClient.connect(deviceId, onDisconnect);
}

/**
 * Disconnect from a BLE device.
 *
 * @param {string} deviceId - The BLE device identifier.
 * @returns {Promise<void>}
 */
export async function disconnectFromDevice(deviceId) {
  await BleClient.disconnect(deviceId);
}

/**
 * Run the PSK authentication handshake against a connected OutdoorLink BLE device.
 *
 * Flow (load-bearing — matches validated hardware behaviour):
 *   1. Read PSK hex string from secure storage via getSharedKey().
 *   2. Start UART RX notifications; accumulate UTF-8 decoded text; flag when "OK" appears.
 *   3. Wait 100 ms for the subscription to fully register.
 *   4. Convert PSK hex to bytes and write to UART TX with writeWithoutResponse.
 *   5. Poll every 200 ms up to 8000 ms for the "OK" flag.
 *   6. Stop notifications.
 *   7. Return { success, response? } or { success: false, error }.
 *
 * @param {string} deviceId - The connected BLE device identifier.
 * @returns {Promise<{ success: boolean, response?: string, error?: string }>}
 */
export async function authenticateDevice(deviceId) {
  try {
    const sharedKey = await getSharedKey();

    let authResponse = '';
    let responseReceived = false;

    await BleClient.startNotifications(deviceId, UART_SERVICE_UUID, UART_RX_CHAR, value => {
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(value);
      authResponse += text;
      if (authResponse.includes('OK')) {
        responseReceived = true;
      }
    });

    await new Promise(resolve => setTimeout(resolve, NOTIFY_SETTLE_MS));

    const keyBytes = hexStringToBytes(sharedKey);
    await BleClient.writeWithoutResponse(deviceId, UART_SERVICE_UUID, UART_TX_CHAR, new DataView(keyBytes.buffer));

    const startTime = Date.now();
    while (!responseReceived && Date.now() - startTime < AUTH_TIMEOUT_MS) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    await BleClient.stopNotifications(deviceId, UART_SERVICE_UUID, UART_RX_CHAR);

    if (responseReceived) {
      return { success: true, response: authResponse.trim() };
    }

    return {
      success: false,
      error: 'Authentication timeout — device did not respond with "OK"',
      response: authResponse.trim() || 'No response',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Authentication failed',
    };
  }
}

/**
 * Toggle a relay on an authenticated OutdoorLink BLE device.
 *
 * Writes the relay number as a bare ASCII string (no CR/LF terminator) to the
 * UART TX characteristic using writeWithoutResponse. The unit toggles the relay
 * on receipt — no separate on/off argument is needed. The write is
 * fire-and-forget: we do not wait for a notify acknowledgement so the UX is
 * never blocked.
 *
 * @param {string} deviceId    - The connected BLE device identifier.
 * @param {number} relayNumber - Physical relay number (e.g. 1, 2, 3).
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function sendRelayCommand(deviceId, relayNumber) {
  try {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(String(relayNumber));
    await BleClient.writeWithoutResponse(deviceId, UART_SERVICE_UUID, UART_TX_CHAR, new DataView(bytes.buffer));
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Relay command failed',
    };
  }
}
